import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}$${Number(v).toFixed(2)}`);
const fmtTs = (ts) => (ts ? new Date(ts * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const TONES = {
  ON: "text-emerald-300 border-emerald-500/60",
  "AUTO-OFF": "text-amber-300 border-amber-500/60",
  "NOT PROFITABLE": "text-red-300 border-red-500/60",
  OFF: "text-slate-400 border-slate-600",
};

const StrategyRow = ({ r, onToggle, busy, profitableOnly }) => {
  const id = r.key.toLowerCase().replace(":", "-");
  const blocked = profitableOnly && !r.profitable_ok;
  const status = !r.enabled ? "OFF" : r.auto_disabled ? "AUTO-OFF" : blocked ? "NOT PROFITABLE" : "ON";
  const tone = TONES[status];
  return (
    <div className="border-b border-slate-800/60 py-1.5" data-testid={`strategy-row-${id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Switch checked={r.enabled} disabled={busy} onCheckedChange={(v) => onToggle(r, v)} className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-600" data-testid={`strategy-toggle-${id}`} />
          <span className="text-slate-100 text-xs truncate">{r.label}</span>
        </div>
        <span className={`label-uppercase text-[9px] px-1.5 py-0.5 border shrink-0 ${tone}`} data-testid={`strategy-status-${id}`}>{status}</span>
      </div>
      <div className="flex flex-wrap gap-x-3 text-[10px] font-mono text-slate-500 mt-0.5 pl-11">
        <span>real {r.trades}t <span className={r.real_pnl >= 0 ? "text-emerald-400" : "text-red-400"}>{usd(r.real_pnl)}</span></span>
        <span>backtest {r.backtest_trades}t <span className={r.backtest_pnl >= 0 ? "text-emerald-400" : "text-red-400"}>{usd(r.backtest_pnl)}</span></span>
        {r.win_rate != null ? <span>win {(r.win_rate * 100).toFixed(0)}%</span> : null}
      </div>
      {profitableOnly && r.enabled && r.profitable_reason ? (
        <div className={`text-[10px] pl-11 ${r.profitable_ok ? "text-emerald-400/70" : "text-red-300/80"}`} data-testid={`strategy-profit-reason-${id}`}>
          {r.profitable_ok ? "Profitable: " : "Blocked: "}{r.profitable_reason}
        </div>
      ) : null}
      {r.auto_disabled && r.auto_reason ? (
        <div className="text-[10px] text-amber-300/80 pl-11" data-testid={`strategy-auto-reason-${id}`}>
          Turned off {fmtTs(r.auto_since)}: {r.auto_reason}. Re-enabled when a fresh backtest is profitable.
        </div>
      ) : null}
    </div>
  );
};

export const StrategiesPanel = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      setD((await axios.get(`${API}/strategies`)).data);
    } catch (e) {
      // keep last
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const onToggle = async (r, v) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/settings`, { [r.setting]: v });
      if (res.data?.rejected?.length) toast.error(`Rejected: ${res.data.rejected.map((x) => x.field || x).join(", ")}`);
      else toast.success(`${r.label} ${v ? "ON" : "OFF"}`);
      await load();
    } catch (e) {
      toast.error("Could not update strategy");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-700/60 bg-slate-900/40 p-3" data-testid="strategies-panel">
      <div className="flex items-center justify-between mb-1">
        <span className="label-uppercase text-cyan-300">Strategies</span>
        <span className={`label-uppercase text-[9px] px-1.5 py-0.5 border ${d?.governor_active ? "text-emerald-300 border-emerald-500/60" : "text-slate-400 border-slate-600"}`} data-testid="strategies-governor">
          {d?.governor_active ? "AUTO-GOVERNOR ON" : "MANUAL"}
        </span>
      </div>
      <div className="text-[10px] text-slate-500 mb-2">
        {d?.governor_active
          ? `Autonomous: any strategy or side losing over ${d?.governor_min_trades ?? 8}+ trades is switched off automatically.`
          : "Manual mode: only your switches apply."}
      </div>
      {d?.pacing ? (
        <div className="border border-slate-800 p-2 mb-2" data-testid="pacing-box">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-100">Daily trade pace</span>
            <span className={`font-mono ${d.pacing.cap_reached ? "text-amber-300" : "text-cyan-300"}`} data-testid="pacing-count">
              {d.pacing.trades_today} / {d.pacing.target} target · max {d.pacing.max}
            </span>
          </div>
          <div className="h-1.5 bg-slate-800 mt-1.5">
            <div className="h-full bg-cyan-500 transition-[width] duration-500"
                 style={{ width: `${Math.min(100, (d.pacing.trades_today / Math.max(d.pacing.max, 1)) * 100)}%` }} />
          </div>
          <div className="text-[10px] text-slate-500 mt-1" data-testid="pacing-state">
            {!d.pacing.enabled ? "Pacing off"
              : d.pacing.cap_reached ? "Daily cap reached — no new entries until 00:00 UTC"
              : d.pacing.pace_factor > 0 ? `Behind pace — entry bars relaxed (score −${d.pacing.score_relax}, candle −${d.pacing.candle_relax}, confluence −${d.pacing.conf_relax}); Profitable-Only stays strict`
              : d.pacing.pace_factor < 0 ? "Ahead of pace — entry bars tightened"
              : "On pace"}
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 border border-slate-800 p-2 mb-2" data-testid="profitable-only-box">
        <div className="min-w-0">
          <div className="text-xs text-slate-100">Profitable-Only mode</div>
          <div className="text-[10px] text-slate-500">
            Trades a strategy and side only if its backtest is positive
            {` (≥ ${d?.profitable_only_min_backtest_trades ?? 3} trades)`} and, after
            {` ${d?.profitable_only_min_real_trades ?? 5}`} real trades, its live result is positive too.
          </div>
        </div>
        <Switch
          checked={!!d?.profitable_only}
          disabled={busy}
          onCheckedChange={(v) => onToggle({ setting: "enable_profitable_only", label: "Profitable-Only" }, v)}
          className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-600 shrink-0"
          data-testid="profitable-only-toggle"
        />
      </div>
      {(d?.strategies || []).map((r) => (
        <StrategyRow key={r.key} r={r} onToggle={onToggle} busy={busy} profitableOnly={!!d?.profitable_only} />
      ))}
    </div>
  );
};

export const AutotunePanel = () => {
  const [d, setD] = useState(null);
  const load = async () => {
    try {
      setD((await axios.get(`${API}/autotune/status`)).data);
    } catch (e) {
      // keep last
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  const run = async () => {
    try {
      const r = await axios.post(`${API}/autotune/run`);
      toast.success(r.data?.message || "Autotune started");
      setTimeout(load, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start autotune");
    }
  };
  const last = d?.last;
  const changes = last?.changes || {};
  return (
    <div className="border border-slate-700/60 bg-slate-900/40 p-3 space-y-2" data-testid="autotune-panel">
      <div className="flex items-center justify-between">
        <span className="label-uppercase text-cyan-300">Autotune</span>
        <span className={`label-uppercase text-[9px] px-1.5 py-0.5 border ${d?.autonomous && d?.enabled ? "text-emerald-300 border-emerald-500/60" : "text-slate-400 border-slate-600"}`} data-testid="autotune-mode">
          {d?.autonomous && d?.enabled ? "AUTO-APPLY" : "REPORT ONLY"}
        </span>
      </div>
      <div className="text-[10px] text-slate-500">
        Daily after learning: tests settings on past candles, confirms on the most recent unseen candles, and applies
        only if they make more profit than the current settings (autonomous mode).
      </div>
      <div className="grid grid-cols-2 gap-2 text-[10px]">
        <div className="border border-slate-800 p-1.5">
          <div className="text-slate-500 label-uppercase text-[9px]">Last run</div>
          <div className="font-mono text-slate-100" data-testid="autotune-last-run">{fmtTs(last?.ts)}</div>
        </div>
        <div className="border border-slate-800 p-1.5">
          <div className="text-slate-500 label-uppercase text-[9px]">Result</div>
          <div className={`font-mono ${last?.applied ? "text-emerald-300" : "text-slate-300"}`} data-testid="autotune-result">
            {!last ? "—" : last.ok === false ? "error" : last.applied ? "applied" : last.best ? "better found" : "kept current"}
          </div>
        </div>
        <div className="border border-slate-800 p-1.5">
          <div className="text-slate-500 label-uppercase text-[9px]">Current · unseen data</div>
          <div className="font-mono text-slate-100" data-testid="autotune-current-test">
            {last?.current_test ? `${usd(last.current_test.total_pnl)} · ${last.current_test.trades ?? 0}t` : "—"}
          </div>
        </div>
        <div className="border border-slate-800 p-1.5">
          <div className="text-slate-500 label-uppercase text-[9px]">Best · unseen data</div>
          <div className="font-mono text-emerald-300" data-testid="autotune-best-test">
            {last?.best?.test ? `${usd(last.best.test.total_pnl)} · ${last.best.test.trades ?? 0}t` : "—"}
          </div>
        </div>
      </div>
      {Object.keys(changes).length ? (
        <div className="text-[10px] font-mono text-slate-300" data-testid="autotune-changes">
          {Object.entries(changes).map(([k, v]) => <div key={k}>{k} → {String(v)}</div>)}
        </div>
      ) : last?.note ? <div className="text-[10px] text-slate-500" data-testid="autotune-note">{last.note}</div> : null}
      <button
        onClick={run}
        disabled={d?.running}
        className="w-full label-uppercase text-[10px] py-1.5 border border-cyan-500/50 text-cyan-300 transition-colors hover:bg-cyan-950/40 disabled:opacity-50"
        data-testid="autotune-run-btn"
      >
        {d?.running ? "Autotuning… (a few minutes)" : "Autotune Now"}
      </button>
    </div>
  );
};
